"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback } from "react";
import { useRouter } from "next/navigation";
import { SectionHeader, FormIndicator } from "fansunited-sports-ui";
import type { FUSportsMatch } from "fansunited-data-layer";
import type { FormEntry } from "fansunited-sports-ui";
import { useTeamLogoUrl } from "@/components/ui/TeamLogo";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTeamFormData, useMatchIdLookup } from "@/hooks/useTeamFormData";
export interface TeamFormProps {
  /** The team ID to show form for */
  teamId: string;
  /** Matches to build the form from */
  matches: FUSportsMatch[];
  /** Team name for display */
  teamName: string;
  /** Team logo URL */
  teamLogo?: string;
  /** Team country code (ISO 3166-1 alpha-2, e.g. "GB") */
  teamCountryCode?: string;
  /** Team country flag URL */
  teamCountryFlag?: string;
  /** Team primary brand color */
  teamPrimaryColor?: string;
  /** Team secondary brand color */
  teamSecondaryColor?: string;
  /** Custom header label. Defaults to team name */
  headerLabel?: string;
  /** Filter by home/away matches. Defaults to "all" (no filter) */
  variant?: "all" | "home" | "away";
  /** Maximum number of matches to show (default: 5) */
  limit?: number;
}

/**
 * Formats a date to DD/MM format
 */
function formatDateDDMM(date: Date | string): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  return `${day}/${month}`;
}

/**
 * TeamForm component displays a team's recent match form.
 * Pure presentational component - receives matches data as props.
 */
export const TeamForm: React.FC<TeamFormProps> = t0 => {
  const $ = _c(44);
  const {
    teamId,
    matches,
    teamName,
    teamLogo,
    teamCountryCode,
    teamCountryFlag,
    headerLabel,
    variant: t1,
    limit: t2
  } = t0;
  const variant = t1 === undefined ? "all" : t1;
  const limit = t2 === undefined ? 5 : t2;
  const router = useRouter();
  const labels = useLabels().teamPage;
  const nav = useNavigation();
  const logoUrl = useTeamLogoUrl();
  let t3;
  if ($[0] !== limit || $[1] !== matches || $[2] !== teamId || $[3] !== variant) {
    t3 = {
      teamId,
      matches,
      variant,
      limit
    };
    $[0] = limit;
    $[1] = matches;
    $[2] = teamId;
    $[3] = variant;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const formData = useTeamFormData(t3);
  const matchIdToNativeId = useMatchIdLookup(matches);
  let t4;
  if ($[5] !== matchIdToNativeId || $[6] !== nav || $[7] !== router) {
    t4 = entry => {
      if (entry.matchId) {
        const matchIdStr = String(entry.matchId);
        const nativeId = matchIdToNativeId.get(matchIdStr) || matchIdStr;
        router.push(nav.match(nativeId));
      }
    };
    $[5] = matchIdToNativeId;
    $[6] = nav;
    $[7] = router;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleFormClick = t4;
  const displayLabel = headerLabel ?? teamName ?? labels.form.recentForm;
  if (!formData || !formData.results || formData.results.length === 0) {
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        typography: {
          name: "text-sm uppercase font-semibold text-foreground"
        }
      };
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== displayLabel) {
      t6 = {
        name: displayLabel,
        styles: t5
      };
      $[10] = displayLabel;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        container: "mb-2"
      };
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t6) {
      t8 = <SectionHeader entity={t6} styles={t7} />;
      $[13] = t6;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== labels.noFormData) {
      t9 = <div className="bg-card rounded-lg p-6"><p className="text-center text-muted-foreground">{labels.noFormData}</p></div>;
      $[15] = labels.noFormData;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== t8 || $[18] !== t9) {
      t10 = <div>{t8}{t9}</div>;
      $[17] = t8;
      $[18] = t9;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    return t10;
  }
  let t5;
  if ($[20] !== formData.competitorId || $[21] !== formData.results || $[22] !== logoUrl || $[23] !== teamCountryCode || $[24] !== teamCountryFlag || $[25] !== teamLogo || $[26] !== teamName) {
    const teamLogoResolved = logoUrl({
      logo: teamLogo,
      countryFlag: teamCountryFlag,
      countryCode: teamCountryCode
    });
    t5 = formData.results.map(result => {
      const scores = result.score?.split("-") || ["0", "0"];
      const {
        isHome
      } = result;
      const opponentLogoResolved = logoUrl({
        logo: result.opponent.logo,
        countryFlag: result.opponent.countryFlag,
        countryCode: result.opponent.countryCode
      });
      return {
        matchId: result.matchId,
        outcome: result.result,
        date: formatDateDDMM(result.date),
        competitorOne: isHome ? {
          id: formData.competitorId,
          name: teamName,
          image: teamLogoResolved,
          score: scores[0]
        } : {
          id: result.opponent.id,
          name: result.opponent.name,
          image: opponentLogoResolved,
          score: scores[0]
        },
        competitorTwo: isHome ? {
          id: result.opponent.id,
          name: result.opponent.name,
          image: opponentLogoResolved,
          score: scores[1]
        } : {
          id: formData.competitorId,
          name: teamName,
          image: teamLogoResolved,
          score: scores[1]
        }
      };
    });
    $[20] = formData.competitorId;
    $[21] = formData.results;
    $[22] = logoUrl;
    $[23] = teamCountryCode;
    $[24] = teamCountryFlag;
    $[25] = teamLogo;
    $[26] = teamName;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const formEntries = t5;
  let t6;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      typography: {
        name: "text-sm uppercase font-semibold text-foreground"
      },
      container: "mb-2"
    };
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== displayLabel) {
    t7 = <SectionHeader entity={{
      name: displayLabel,
      styles: t6
    }} />;
    $[29] = displayLabel;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== labels.form.draw || $[32] !== labels.form.loss || $[33] !== labels.form.vs || $[34] !== labels.form.win) {
    t8 = {
      draw: labels.form.draw,
      loss: labels.form.loss,
      vs: labels.form.vs,
      win: labels.form.win
    };
    $[31] = labels.form.draw;
    $[32] = labels.form.loss;
    $[33] = labels.form.vs;
    $[34] = labels.form.win;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      item: {
        container: "shadow-md cursor-pointer",
        padding: "py-6 px-2 xl:px-6 lg:px-5",
        borderRadius: "md",
        border: "border-0 border-t-4",
        typography: {
          label: "text-[11px]"
        }
      },
      gap: "2"
    };
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== formEntries || $[38] !== handleFormClick || $[39] !== t8) {
    t10 = <FormIndicator form={formEntries} labels={t8} styles={t9} variant="detailed" mode="responsive" onClick={handleFormClick} />;
    $[37] = formEntries;
    $[38] = handleFormClick;
    $[39] = t8;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== t10 || $[42] !== t7) {
    t11 = <div>{t7}{t10}</div>;
    $[41] = t10;
    $[42] = t7;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
};